// Notice a login's link to its member while the app is still signed in.
//
// A login with no member (`profile.hasNoMember`) is waiting for its CG leader to
// link it, and the link happens in Acts, on somebody else's phone. Nothing tells
// this one: the profile is fetched once per launch (`fetchProfile` is a no-op
// once it has loaded) and the app is usually resumed, not relaunched. So the
// app went on reading `hasNoMember: true` and no member id while the API was
// already answering every request as the member — Next Steps never hydrated the
// member's keys and never enrolled, until a sign-out (dev, 2026-11-03).
//
// So, while the login is waiting and only then, every return to the foreground
// re-reads the profile. A member costs nothing here. The re-read is all the
// rest needs: the profile id going from null to the member's is what the key
// hydration (key-hydration.js) and, through it, the enrolment and migration
// triggers already key on, and every screen that branches on `hasNoMember`
// reads the store.
//
// Two things were read for the LOGIN and do not follow the id by themselves,
// and the link drops them: the devices snapshot (`courses-devices`), which the
// sealing hook and the outbox drain trust as "the status was read this
// session", and the member's own feature flags, loaded once per token.

/**
 * @param {{ store: object, onLinked?: () => void }} args `onLinked` runs once
 *   per link, after the login's status snapshot is dropped
 * @returns {{ refresh: () => Promise<void>, stop: () => void }} `refresh` is
 *   the resume hook; `stop` removes the watcher, for specs
 */
export function installLinkRefresh({ store, onLinked = () => {} }) {
  if (!store) throw new TypeError('installLinkRefresh needs the root Vuex store');

  let inFlight = null;

  const refresh = () => {
    if (!store.state.user?.token || store.state.profile?.hasNoMember !== true) {
      return Promise.resolve();
    }
    if (!inFlight) {
      inFlight = Promise.resolve(store.dispatch('profile/forceFetchProfile'))
        .catch(error => {
          // Still waiting as far as this phone knows; the next resume asks again.
          console.warn('[MemberSplit] The profile did not reload on resume:', error);
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  // A member id only once the login is no longer waiting: a sign-out also
  // turns `hasNoMember` false, and has its own reset.
  const linkedMember = state => {
    if (state.profile?.hasNoMember === true) return -1;
    const memberId = Number(state.profile?.id);
    return Number.isInteger(memberId) && memberId > 0 ? memberId : 0;
  };

  const stop = store.watch(linkedMember, (memberId, previous) => {
    if (previous !== -1 || memberId <= 0) return;
    store.commit('courses-devices/reset');
    onLinked();
  });

  return { refresh, stop };
}
